// Adapters: frozen ShareSnapshot (the ONLY data the public side receives) ->
// the console's local domain types, so the client view can REUSE the console's
// Viewer / DefectList / SourceGallery / remediation primitives unchanged.
//
// The snapshot is already fully resolved (absolute R2 URLs, resolved brand,
// per-defect frozen sources). These helpers only reshape it — no lookups, no
// network, no photos table (§9).
import type { ActiveOrtho } from "../app/defects/geo";
import type { Defect, PhotoSurface, Severity, SourceImage } from "../app/defects/types";
import type { RemItem } from "../app/remediation/data";
import type {
  ShareDefect, ShareOrtho, SharePhoto, ShareRemItem, ShareSnapshot,
} from "../worker/modules/share-types";

export type { ShareSnapshot } from "../worker/modules/share-types";

// Every URL the client loads is a public, absolute R2 URL frozen at share
// creation. The browser loads imagery directly; the worker never proxies it.

/**
 * A snapshot ortho -> the viewer's ActiveOrtho on the RASTER (CRS.Simple) tile
 * path. We deliberately DROP any mercator pyramid (tileUrl3857/bounds): the
 * snapshot carries no WGS84 corner quad, so a geographic remap would drift the
 * pins. Pure pixel space keeps every defect pin exactly where it was authored.
 */
export function orthoToActive(o: ShareOrtho): ActiveOrtho {
  return {
    id: o.orthoKey,
    tileUrl: o.tileUrl,
    width: o.width,
    height: o.height,
    maxZoom: o.maxZoom,
    tileSize: o.tileSize,
    gsdM: null, // no GSD in the snapshot -> measure tools read "—" (and are off anyway)
    bounds3857: null,
    tileUrl3857: null,
    minZoom3857: null,
    maxZoom3857: null,
  };
}

/** A frozen ad hoc photo surface -> the viewer's PhotoSurface. */
export function photoToSurface(p: SharePhoto): PhotoSurface {
  return { id: p.id, ref: 0, label: p.label, src: p.src, width: p.width, height: p.height };
}

/** A frozen defect's resolved photo evidence -> the viewer's SourceImage[]. */
export function defectSources(d: ShareDefect): SourceImage[] {
  return d.sources.map((s, i) => ({ id: `${d.id}:${i}`, src: s.thumbUrl, full: s.fullUrl }));
}

/**
 * A frozen defect -> the local Defect. Provenance is intentionally absent from
 * the snapshot (a client sees findings, not who edited them), so every marker
 * reads as an AI-detected finding: uniform orange pin, neutral "AI" chip.
 */
export function defectToLocal(d: ShareDefect): Defect {
  return {
    id: d.id,
    orthoViewId: d.orthoKey || undefined,
    view: d.view, // ad hoc photo surface id when photo-tagged; absent = orthomosaic
    ref: d.ref,
    title: d.title,
    category: d.category,
    severity: d.severity as Severity,
    confidence: d.confidence,
    x: d.x,
    y: d.y,
    shape: d.shape,
    desc: d.desc,
    action: d.action,
    source: "ai",
    modified: false,
    sourceImages: [], // sources resolve via the frozen resolver, never the photos table
  };
}

/** A frozen remediation item -> the console RemItem (reuses its pills/detail). */
export function remToItem(r: ShareRemItem, createdAt: number): RemItem {
  return {
    id: r.id,
    ref: r.ref,
    title: r.title,
    scope: r.scope,
    priority: r.priority,
    status: r.status,
    trades: r.trades,
    defectIds: r.defectIds,
    costs: r.costs,
    createdAt,
    source: "ai",
  };
}

export interface SnapshotModel {
  snapshot: ShareSnapshot;
  defects: Defect[];
  /** Ad hoc photo surfaces (empty unless the share included them). */
  photos: PhotoSurface[];
  /** defectId -> frozen SourceImage[] (drives popover strip, gallery, rem cards). */
  sourcesById: Map<string, SourceImage[]>;
  remItems: RemItem[];
}

/** One-time reshape of a loaded snapshot into everything the UI consumes. */
export function buildModel(snapshot: ShareSnapshot): SnapshotModel {
  const defects = snapshot.defects.map(defectToLocal);
  const photos = (snapshot.photos ?? []).map(photoToSurface);
  const photoById = new Map(photos.map((p) => [p.id, p]));
  const sourcesById = new Map<string, SourceImage[]>();
  snapshot.defects.forEach((d) => {
    // A photo-tagged defect's single source IS the ad hoc photo it lives on
    // (the snapshot carries no drone captures for it). Ortho defects resolve
    // their frozen drone sources as before.
    if (d.view) {
      const p = photoById.get(d.view);
      sourcesById.set(d.id, p ? [{ id: `adhoc:${p.id}`, src: p.src, full: p.src }] : []);
    } else {
      sourcesById.set(d.id, defectSources(d));
    }
  });
  const remItems = (snapshot.remediation ?? []).map((r) => remToItem(r, snapshot.createdAt));
  return { snapshot, defects, photos, sourcesById, remItems };
}
